<template>
    <div>
        <div
            role="meter"
            :aria-label="`${dimensionLabel('left')} vs ${dimensionLabel('right')}`"
            :aria-valuenow="binnedScore"
            :aria-valuemin="-3"
            :aria-valuemax="3"
            :aria-valuetext="dimensionValueText(binnedScore)"
            class="relative h-2.5 bg-surface-sunken rounded-full">
            <div class="absolute top-0 bottom-0 rounded-full transition-all duration-1000" :class="dimensionColor(code)" :style="pillStyle(binnedScore)" />
        </div>
        <div class="flex justify-between mt-1.5">
            <span class="flex items-center gap-1.5" :class="binnedScore < 0 ? 'body-small-bold text-gray-700' : 'body-small text-gray-500'">
                <PerspectiveDot v-if="binnedScore < 0" :dimension="code" size="sm" />
                {{ dimensionLabel("left") }}
            </span>
            <span class="flex items-center gap-1.5" :class="binnedScore >= 0 ? 'body-small-bold text-gray-700' : 'body-small text-gray-500'">
                {{ dimensionLabel("right") }}
                <PerspectiveDot v-if="binnedScore >= 0" :dimension="code" size="sm" />
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { type Dimension, DIMENSION_COLORS } from "~vue/pages/perspectiveTypes";
import PerspectiveDot from "~vue/Perspective/PerspectiveDot.vue";
import { scoreToPercent } from "~vue/utils/perspectiveUtils";

type DimensionBarProps = {
    code: Dimension;
    binnedScore: number;
    leftLabel: string;
    rightLabel: string;
};

const { leftLabel, rightLabel } = defineProps<DimensionBarProps>();

function dimensionLabel(side: "left" | "right"): string {
    return side === "left" ? leftLabel : rightLabel;
}

function dimensionColor(code: Dimension): string {
    return DIMENSION_COLORS[code];
}

function dimensionValueText(binnedScore: number): string {
    if (binnedScore === 0) {
        return "balanced";
    }
    const dominant = binnedScore > 0 ? dimensionLabel("right") : dimensionLabel("left");
    const strength = Math.abs(binnedScore) <= 1 ? "slight" : Math.abs(binnedScore) <= 2 ? "moderate" : "strong";
    return `${strength} preference for ${dominant}`;
}

function pillStyle(binnedScore: number): { left: string; width: string } {
    const percent = scoreToPercent(binnedScore);
    const left = Math.max(0, Math.min(percent - 14, 72));
    return { left: `${left}%`, width: "27%" };
}
</script>
